/**
 * Session manager utility to handle token expiration and session timeout
 */

import { useAuthStore, useUiStore } from '@/stores'
import router from '../router';

// Constants for timeouts
const SESSION_CHECK_INTERVAL = 5 * 60 * 1000; // Check every 5 minutes
const INACTIVITY_THRESHOLD = 20 * 60 * 1000; // 20 minutes of inactivity
const TIMEOUT_WARNING_THRESHOLD = 2 * 60 * 1000; // Show warning 3 minutes before timeout
let sessionCheckTimer: NodeJS.Timeout | null = null;
let lastActivityTime = Date.now();
let timeoutWarningShown = false;

const sessionManager = {
  /**
   * Initialize the session manager
   */
  init() {
    // Reset last activity time
    this.resetActivityTimer();
    
    // Clear any existing timer
    if (sessionCheckTimer !== null) {
      clearInterval(sessionCheckTimer);
    }
    
    // Start monitoring session
    sessionCheckTimer = setInterval(() => this.checkSession(), SESSION_CHECK_INTERVAL);
    
    // Set up activity listeners
    this.setupActivityListeners();
  },
  
  /**
   * Set up activity listeners to track user activity
   */
  setupActivityListeners() {
    // List of events to monitor
    const events = ['mousedown', 'keypress', 'scroll', 'touchstart'];
    
    // Add listeners
    events.forEach(eventType => {
      document.addEventListener(eventType, this.resetActivityTimer.bind(this));
    });
  },
  
  /**
   * Reset activity timer
   */
  resetActivityTimer() {
    lastActivityTime = Date.now();
    timeoutWarningShown = false;
  },
  
  /**
   * Check if the session is still valid
   */
  checkSession() {
    try {
      const authStore = useAuthStore();
      
      // If not authenticated, nothing to do
      if (!authStore.isAuthenticated) {
        return;
      }
      
      // Check token expiration
      const token = authStore.getAccessToken;
      if (token && authStore.isAuthenticated) {
        // Get inactivity time
        const inactivityTime = Date.now() - lastActivityTime;
        
        // Check if we're approaching timeout
        if (inactivityTime >= INACTIVITY_THRESHOLD - TIMEOUT_WARNING_THRESHOLD && !timeoutWarningShown) {
          timeoutWarningShown = true;
          this.showTimeoutWarning(Math.round(TIMEOUT_WARNING_THRESHOLD / 60000));
        }
        
        // Check if we've timed out
        if (inactivityTime >= INACTIVITY_THRESHOLD) {
          this.handleTimeout();
          return;
        }
      }
    } catch (error) {
      console.warn('Error during session check:', error);
    }
  },
  
  /**
   * Show timeout warning to user
   * @param {number} minutesLeft - Minutes until session expires
   */
  showTimeoutWarning(minutesLeft: number) {
    const warningMessage = document.createElement('div');
    warningMessage.id = 'session-timeout-warning';
    warningMessage.innerHTML = `
      <div class="session-timeout-container">
        <div class="session-timeout-content">
          <h3>Session Timeout Warning</h3>
          <p>Your session will expire in approximately ${minutesLeft} minutes due to inactivity.</p>
          <div class="session-timeout-actions">
            <button id="session-extend-btn">Stay Logged In</button>
            <button id="session-logout-btn">Logout Now</button>
          </div>
        </div>
      </div>
    `;
    
    // Add styles
    const style = document.createElement('style');
    style.textContent = `
      #session-timeout-warning {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: rgba(0, 0, 0, 0.5);
        z-index: 10000;
        display: flex;
        justify-content: center;
        align-items: center;
      }
      .session-timeout-container {
        background-color: white;
        border-radius: 8px;
        padding: 20px;
        max-width: 400px;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      }
      .session-timeout-content h3 {
        margin-top: 0;
        color: #dc3545;
      }
      .session-timeout-actions {
        display: flex;
        justify-content: space-between;
        margin-top: 20px;
      }
      .session-timeout-actions button {
        padding: 8px 16px;
        border-radius: 4px;
        border: none;
        cursor: pointer;
        font-weight: bold;
      }
      #session-extend-btn {
        background-color: #007bff;
        color: white;
      }
      #session-logout-btn {
        background-color: #6c757d;
        color: white;
      }
    `;
    
    document.body.appendChild(style);
    document.body.appendChild(warningMessage);
    
    // Add event listeners
    document.getElementById('session-extend-btn')!.addEventListener('click', () => {
      this.extendSession();
      document.body.removeChild(warningMessage);
      document.body.removeChild(style);
    });
    
    document.getElementById('session-logout-btn')!.addEventListener('click', () => {
      try {
        const authStore = useAuthStore();
        authStore.logout();
      } catch (error) {
        console.warn('Error during logout:', error);
      }
      document.body.removeChild(warningMessage);
      document.body.removeChild(style);
    });
  },
  
  /**
   * Handle session timeout
   */
  handleTimeout() {
    try {
      const authStore = useAuthStore();
      const uiStore = useUiStore();
      
      // Log the user out
      uiStore.setError('Your session has timed out due to inactivity');
      authStore.logout();
    } catch (error) {
      console.warn('Error during timeout handling:', error);
    }
    
    // Remove any existing warning
    const warning = document.getElementById('session-timeout-warning');
    if (warning) {
      document.body.removeChild(warning);
    }
    
    // Navigate to home page if not already there
    if (router.currentRoute.value.path !== '/') {
      router.push('/');
    }
  },
  
  /**
   * Extend the current session
   */
  async extendSession() {
    // Reset activity timer
    this.resetActivityTimer();
    
    try {
      // Try to refresh token
      const authStore = useAuthStore();
      const refreshed = await authStore.performTokenRefresh();
      if (!refreshed) {
        this.handleTimeout();
      }
    } catch (error) {
      console.error('Error extending session:', error);
      this.handleTimeout();
    }
  },
  
  /**
   * Clean up resources when session manager is destroyed
   */
  cleanup() {
    if (sessionCheckTimer !== null) {
      clearInterval(sessionCheckTimer);
      sessionCheckTimer = null;
    }
    
    // Remove event listeners
    const events = ['mousedown', 'keypress', 'scroll', 'touchstart'];
    events.forEach(eventType => {
      document.removeEventListener(eventType, this.resetActivityTimer);
    });
  }
};

export default sessionManager;
